import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN,
  COMPANY_OWNERSHIP_API_SERVICE_TOKEN,
  DATA_SOURCE_API_SERVICE_TOKEN,
  PERSON_API_SERVICE_TOKEN,
} from '@nk/api-lib';
import {
  CompanyShareOwnershipUltimateCompositeEntity,
  PersonCompanyRolePersonFlagCompositeEntity,
  SimpleCollection,
} from '@nk/data-entities-lib';
import {
  CardListComponent,
  ErrorAlertComponent,
  HelpPopoverComponent,
  NkCardListConfig,
  NkCardListData,
  PercentagePipe,
} from '@nk/ui-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { BehaviorSubject, catchError, combineLatest, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { MonitoringConnectionService } from '../../services/monitoring-connection.service';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { ScreeningCompositeEntity } from '../../utils/company-screening.utils';
import {
  EMPTY_CARD_LIST_DATA,
  MONITORING_CONNECTION_CARD_LIST_CONFIG,
  MonitoringConnectionCardListItem,
  transformToMonitoringConnectionCardListItems,
} from '../../utils/monitoring-connection.utils';
import { DEFAULT_COMPANY_ROLES_PAGE_LIMIT } from '../../utils/screening.utils';

@Component({
  selector: 'nk-monitoring-connection-card-list',
  imports: [AsyncPipe, TranslateModule, PopoverModule, CardListComponent, HelpPopoverComponent, ErrorAlertComponent],
  templateUrl: './monitoring-connection-card-list.component.html',
  providers: [PercentagePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringConnectionCardListComponent implements OnInit {
  public collapsibleDetails = input<boolean>(true);
  public screeningSuggestionsLoading$!: Observable<boolean>;
  public screeningEntities$!: Observable<ScreeningCompositeEntity[]>;
  public cardListConfig: NkCardListConfig<MonitoringConnectionCardListItem> = MONITORING_CONNECTION_CARD_LIST_CONFIG;
  public data$!: Observable<NkCardListData<MonitoringConnectionCardListItem>>;
  public isLoading = signal(false);
  private _filters$ = new BehaviorSubject<{
    page: number;
    limit: number;
  }>({ page: 0, limit: 100 });
  public error = signal<HttpErrorResponse | null>(null);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _monitoringConnectionService = inject(MonitoringConnectionService);
  private _companyLegalRightService = inject(COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN);
  private _dataSourceService = inject(DATA_SOURCE_API_SERVICE_TOKEN);
  private _companyOwnershipService = inject(COMPANY_OWNERSHIP_API_SERVICE_TOKEN);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _ownershipPercentagePipe = inject(PercentagePipe);
  private _translateService = inject(TranslateService);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _monitoringConnectionCardListLoadingId = computed(
    () => `${this.constructor.name}_monitoringConnectionCardList`,
  );

  public ngOnInit(): void {
    this.isLoading.set(true);
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this._monitoringConnectionCardListLoadingId(), true);
    }
    this.data$ = combineLatest([this._screeningInputParamsService.screeningEntitiesInputParams$, this._filters$]).pipe(
      switchMap(([params, filters]) => {
        this.error.set(null);
        if (!params?.screeningConfigUuid || !params?.monitoringOriginEntityUuid || !params?.companyUuid) {
          return of(EMPTY_CARD_LIST_DATA);
        }
        const offset = filters.page * filters.limit;
        return combineLatest([
          this._monitoringConnectionService.monitoringConnectionCompositeCollectionCache.get(
            params.screeningConfigUuid,
            params.monitoringOriginEntityUuid,
            undefined,
            filters.limit,
            offset,
          ),
          this._fetchOwnershipByCompany(params.companyUuid),
          this._fetchCompanyRoles(params.companyUuid),
          this._companyLegalRightService.companyLegalRightCompositeCache.get(params.companyUuid),
          this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache
            .get(params.screeningConfigUuid, params.monitoringOriginEntityUuid, undefined, 100, 0)
            .pipe(catchError(() => of({ elements: [] }))),
        ]).pipe(
          map(
            ([
              monitoringConnectionCompositeCollection,
              ownershipCompositeCollection,
              personCompanyRolePersonFlagCompositeEntities,
              legalRightComposites,
              screeningEntityCaseCompositeCollection,
            ]) => {
              const entities = transformToMonitoringConnectionCardListItems(
                monitoringConnectionCompositeCollection?.elements,
                ownershipCompositeCollection?.elements,
                personCompanyRolePersonFlagCompositeEntities,
                legalRightComposites,
                screeningEntityCaseCompositeCollection.elements,
                this._translateService,
                this._ownershipPercentagePipe,
              );
              return {
                entities: entities,
                pagination: {
                  limit: filters.limit,
                  page: filters.page,
                  totalCount: entities.length,
                },
              };
            },
          ),
          tap(() => this.isLoading.set(false)),
          finalize(() => {
            if (this._reportLoadingStateService) {
              this._reportLoadingStateService.setLoadingState(this._monitoringConnectionCardListLoadingId(), false);
            }
          }),
          catchError((error) => {
            this.error.set(error);
            console.error('Error fetching monitoring connections:', error);
            this.isLoading.set(false);
            return of(EMPTY_CARD_LIST_DATA);
          }),
        );
      }),
    );
  }

  private _fetchOwnershipByCompany(
    companyUuid: string | undefined,
  ): Observable<SimpleCollection<CompanyShareOwnershipUltimateCompositeEntity> | null> {
    if (!companyUuid) {
      return of(null);
    }
    // ASSUMES NORWEGIAN OWNERSHIP!
    const dataSourceKey = 'ownership_norway';
    return this._dataSourceService.dataSourceAvailableYearCache.get(dataSourceKey).pipe(
      switchMap((years) => {
        return this._companyOwnershipService.companyShareOwnershipUltimateCompositeCollection.get({
          shareIssuerCompanyIdentifier: companyUuid,
          offset: 0,
          year: years[years.length - 1],
        });
      }),
      map((payload) => (payload ? payload : null)),
    );
  }

  private _fetchCompanyRoles(
    companyUuid: string | undefined,
  ): Observable<PersonCompanyRolePersonFlagCompositeEntity[]> {
    if (!companyUuid) {
      return of([]);
    }
    const currentRolesFlag = true;
    return this._personService.personCompanyRolePersonFlagListByCompanyCache
      .get(companyUuid, currentRolesFlag, [], DEFAULT_COMPANY_ROLES_PAGE_LIMIT, 0)
      .pipe(
        map((data) =>
          data.elements.sort((a, b) => (a.person_company_role.from_date < b.person_company_role.from_date ? -1 : 1)),
        ),
      );
  }
}
